import React, { useState } from 'react';
import { Col, Row } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, Body3Regular, Subtitle1Bold, CaptionLight } from '../../lib/font';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';

interface Props {
    isVisible: boolean;
    onClose: () => void;
    centerName?: string;
}

const KAKAO_CHANNEL_URL = 'http://pf.kakao.com/_ICxhcb/chat';

const PublicMemberBulkUploadModal = ({ isVisible, onClose, centerName }: Props) => {
    const [isCoachSectionOpen, setIsCoachSectionOpen] = useState(false);

    /** F열(col=5)부터 안내 텍스트를 시트에 삽입하는 헬퍼 */
    const addGuideToSheet = (
        ws: XLSX.WorkSheet,
        guideCol: number,
        lines: { text: string; style?: XLSX.CellStyle }[],
    ) => {
        lines.forEach((line, row) => {
            const addr = XLSX.utils.encode_cell({ r: row, c: guideCol });
            ws[addr] = { v: line.text, t: 's', ...(line.style ? { s: line.style } : {}) };
        });
        // ref 범위를 확장해줘야 엑셀이 F열을 인식함
        const curRef = XLSX.utils.decode_range(ws['!ref'] || 'A1');
        const lastGuideRow = lines.length - 1;
        if (lastGuideRow > curRef.e.r) curRef.e.r = lastGuideRow;
        if (guideCol > curRef.e.c) curRef.e.c = guideCol;
        ws['!ref'] = XLSX.utils.encode_range(curRef);
    };

    const GUIDE_TITLE_STYLE: XLSX.CellStyle = {
        font: { bold: true, sz: 14, color: { rgb: '1E40AF' } },
    };
    const GUIDE_WARN_STYLE: XLSX.CellStyle = {
        font: { bold: true, sz: 11, color: { rgb: 'B45309' } },
        fill: { fgColor: { rgb: 'FEF3C7' } },
    };
    const GUIDE_SECTION_STYLE: XLSX.CellStyle = {
        font: { bold: true, sz: 11, color: { rgb: '111827' } },
    };
    const GUIDE_BODY_STYLE: XLSX.CellStyle = {
        font: { sz: 10, color: { rgb: '4B5563' } },
    };
    const GUIDE_LINK_STYLE: XLSX.CellStyle = {
        font: { sz: 10, color: { rgb: '2563EB' }, underline: true },
    };

    const handleDownloadPublicTemplate = () => {
        const wb = XLSX.utils.book_new();

        // ── 회원목록 데이터 (A~E열) ──
        const headers = ['이름', '휴대폰번호', '성별(선택)', '생년월일(선택)', '메모(선택)'];
        const exampleRows = [
            ['홍길동', '010-1234-5678', '남', '1990-01-15', '요가 관심'],
            ['김영희', '010-9876-5432', '여', '1985-06-20', ''],
            ['이철수', '010-5555-1234', '남', '', '기존 회원 재등록'],
        ];

        const ws = XLSX.utils.aoa_to_sheet([headers, ...exampleRows]);

        // 컬럼 너비 (A~E + 빈칸 + F 안내)
        ws['!cols'] = [
            { wch: 15 },  // A 이름
            { wch: 18 },  // B 휴대폰번호
            { wch: 14 },  // C 성별
            { wch: 18 },  // D 생년월일
            { wch: 30 },  // E 메모
            { wch: 60 },  // F 안내
        ];

        // 헤더 스타일 — 필수(A,B)는 파란색, 선택(C,D,E)은 형광 노란
        for (let col = 0; col < headers.length; col++) {
            const addr = XLSX.utils.encode_cell({ r: 0, c: col });
            if (!ws[addr]) continue;
            const isRequired = col < 2;
            ws[addr].s = {
                font: { bold: true, sz: 12, color: { rgb: isRequired ? 'FFFFFF' : '374151' } },
                fill: { fgColor: { rgb: isRequired ? '3B82F6' : 'FDE68A' } },
                alignment: { horizontal: 'center', vertical: 'center' },
            };
        }

        // 예시 행 스타일 (연한 회색 이탤릭)
        for (let row = 1; row <= exampleRows.length; row++) {
            for (let col = 0; col < headers.length; col++) {
                const addr = XLSX.utils.encode_cell({ r: row, c: col });
                if (!ws[addr]) continue;
                ws[addr].s = { font: { color: { rgb: '9CA3AF' }, italic: true } };
            }
        }

        // ── F열: 안내 텍스트 ──
        addGuideToSheet(ws, 5, [
            { text: '📋 라포(Rappo) 퍼블릭 회원 일괄 등록 안내', style: GUIDE_TITLE_STYLE },
            { text: '' },
            { text: '⚠️ 이 템플릿은 "퍼블릭 회원" 등록용입니다.', style: GUIDE_WARN_STYLE },
            { text: '   담당 강사가 배정된 수강(PT) 회원은 별도 템플릿을 이용해주세요.', style: GUIDE_WARN_STYLE },
            { text: '' },
            { text: '[ 작성 방법 ]', style: GUIDE_SECTION_STYLE },
            { text: '1. 이름과 휴대폰번호는 반드시 입력해야 합니다. (필수)', style: GUIDE_BODY_STYLE },
            { text: '2. 휴대폰번호는 하이픈(-)을 포함하여 입력해주세요. (예: 010-1234-5678)', style: GUIDE_BODY_STYLE },
            { text: '3. 성별은 "남" 또는 "여"로 입력해주세요. (선택)', style: GUIDE_BODY_STYLE },
            { text: '4. 생년월일은 YYYY-MM-DD 형식으로 입력해주세요. (선택)', style: GUIDE_BODY_STYLE },
            { text: '5. 메모는 자유롭게 입력 가능합니다. (선택)', style: GUIDE_BODY_STYLE },
            { text: '6. 예시 데이터(회색 글씨)는 삭제 후 실제 정보를 입력해주세요.', style: GUIDE_BODY_STYLE },
            { text: '' },
            { text: '[ 제출 방법 ]', style: GUIDE_SECTION_STYLE },
            { text: '1. 작성을 완료한 후 이 파일을 저장해주세요.', style: GUIDE_BODY_STYLE },
            { text: '2. 아래 라포 카카오 채널로 파일을 전송해주세요.', style: GUIDE_BODY_STYLE },
            { text: `→ ${KAKAO_CHANNEL_URL}`, style: GUIDE_LINK_STYLE },
            { text: '3. 운영팀이 확인 후 등록을 진행해드립니다.', style: GUIDE_BODY_STYLE },
            { text: '' },
            { text: '[ 주의사항 ]', style: GUIDE_SECTION_STYLE },
            { text: '- 이미 등록된 휴대폰번호는 자동으로 건너뜁니다.', style: GUIDE_BODY_STYLE },
            { text: '- 한 번에 최대 500명까지 등록 가능합니다.', style: GUIDE_BODY_STYLE },
            { text: '- 문의사항은 카카오 채널로 편하게 연락주세요!', style: GUIDE_BODY_STYLE },
        ]);

        XLSX.utils.book_append_sheet(wb, ws, '회원목록');

        const fileName = centerName
            ? `[라포] 퍼블릭회원_일괄등록_${centerName}.xlsx`
            : '[라포] 퍼블릭회원_일괄등록_템플릿.xlsx';
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), fileName);
    };

    const handleDownloadCoachTemplate = () => {
        const wb = XLSX.utils.book_new();

        // ── 회원목록 데이터 (A~C열) ──
        const headers = ['담당강사', '회원명', '휴대폰번호'];
        const exampleRows = [
            ['홍길동', '김철수', '010-1111-2222'],
            ['홍길동', '박영희', '010-3333-4444'],
            ['이영수', '최민수', '010-5555-6666'],
        ];

        const ws = XLSX.utils.aoa_to_sheet([headers, ...exampleRows]);

        // 컬럼 너비 (A~C + 빈칸 + E 안내)
        ws['!cols'] = [
            { wch: 15 },  // A 담당강사
            { wch: 15 },  // B 회원명
            { wch: 18 },  // C 휴대폰번호
            { wch: 4 },   // D 빈칸
            { wch: 60 },  // E 안내
        ];

        // 헤더 스타일 (녹색)
        for (let col = 0; col < headers.length; col++) {
            const addr = XLSX.utils.encode_cell({ r: 0, c: col });
            if (!ws[addr]) continue;
            ws[addr].s = {
                font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: '059669' } },
                alignment: { horizontal: 'center', vertical: 'center' },
            };
        }

        // 예시 행 스타일
        for (let row = 1; row <= exampleRows.length; row++) {
            for (let col = 0; col < headers.length; col++) {
                const addr = XLSX.utils.encode_cell({ r: row, c: col });
                if (!ws[addr]) continue;
                ws[addr].s = { font: { color: { rgb: '9CA3AF' }, italic: true } };
            }
        }

        // ── E열(col=4): 안내 텍스트 ──
        addGuideToSheet(ws, 4, [
            { text: '📋 라포(Rappo) 수강(PT) 회원 일괄 등록 안내', style: GUIDE_TITLE_STYLE },
            { text: '' },
            { text: 'ℹ️ 이 템플릿은 담당 강사가 배정된 "수강(PT) 회원" 등록용입니다.', style: { font: { bold: true, sz: 11, color: { rgb: '065F46' } }, fill: { fgColor: { rgb: 'D1FAE5' } } } },
            { text: '' },
            { text: '[ 작성 방법 ]', style: GUIDE_SECTION_STYLE },
            { text: '1. 담당강사, 회원명, 휴대폰번호 모두 필수 입력 항목입니다.', style: GUIDE_BODY_STYLE },
            { text: '2. 휴대폰번호는 하이픈(-)을 포함하여 입력해주세요. (예: 010-1234-5678)', style: GUIDE_BODY_STYLE },
            { text: '3. 담당강사란에는 라포에 등록된 강사님 성함을 입력해주세요.', style: GUIDE_BODY_STYLE },
            { text: '4. 예시 데이터(회색 글씨)는 삭제 후 실제 정보를 입력해주세요.', style: GUIDE_BODY_STYLE },
            { text: '' },
            { text: '[ 제출 방법 ]', style: GUIDE_SECTION_STYLE },
            { text: '1. 작성을 완료한 후 이 파일을 저장해주세요.', style: GUIDE_BODY_STYLE },
            { text: '2. 아래 라포 카카오 채널로 파일을 전송해주세요.', style: GUIDE_BODY_STYLE },
            { text: `→ ${KAKAO_CHANNEL_URL}`, style: GUIDE_LINK_STYLE },
            { text: '3. 운영팀이 확인 후 등록을 진행해드립니다.', style: GUIDE_BODY_STYLE },
            { text: '' },
            { text: '[ 주의사항 ]', style: GUIDE_SECTION_STYLE },
            { text: '- 담당강사 이름은 라포에 등록된 이름과 정확히 일치해야 합니다.', style: GUIDE_BODY_STYLE },
            { text: '- 문의사항은 카카오 채널로 편하게 연락주세요!', style: GUIDE_BODY_STYLE },
        ]);

        XLSX.utils.book_append_sheet(wb, ws, '회원목록');

        const fileName = centerName
            ? `[라포] 수강회원_일괄등록_${centerName}.xlsx`
            : '[라포] 수강회원_일괄등록_템플릿.xlsx';
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), fileName);
    };

    if (!isVisible) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={onClose}
        >
            <Col
                className="bg-white rounded-[16px] p-[32px] w-[520px] max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <Subtitle1Bold>퍼블릭 회원 일괄 등록</Subtitle1Bold>
                <Body3Regular color={colors.LAVEL_1} className="mt-[4px]">
                    엑셀 템플릿을 다운로드하여 작성 후, 카카오 채널로 보내주세요.
                </Body3Regular>

                {/* 퍼블릭 회원 안내 배지 */}
                <Col
                    className="mt-[16px] px-[12px] py-[10px] rounded-[8px] gap-[6px]"
                    style={{ backgroundColor: '#FFF7ED', border: '1px solid #FDBA74' }}
                >
                    <Row className="gap-[8px] items-start">
                        <span style={{ fontSize: 13, lineHeight: '18px' }}>📣</span>
                        <Body3Regular style={{ fontWeight: 600, color: '#9A3412', fontSize: 12, lineHeight: '18px' }}>
                            이 기능은 퍼블릭 회원(담당 강사 없는 센터 회원) 등록용입니다.
                        </Body3Regular>
                    </Row>
                    <Row className="gap-[8px] items-start">
                        <span style={{ fontSize: 13, lineHeight: '18px' }}>📎</span>
                        <CaptionLight style={{ color: '#9A3412', lineHeight: '18px' }}>
                            다른 서비스에서 내려 받은 퍼블릭 회원 엑셀 파일이 있다면 전달해주세요.
                        </CaptionLight>
                    </Row>
                </Col>

                {/* 스텝 안내 */}
                <Col className="mt-[24px] gap-[16px]">
                    {/* Step 1 */}
                    <Row className="gap-[14px] items-start">
                        <div
                            className="flex-shrink-0 w-[32px] h-[32px] rounded-full flex items-center justify-center"
                            style={{ backgroundColor: '#EFF6FF' }}
                        >
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#3B82F6' }}>1</span>
                        </div>
                        <Col className="flex-1 pt-[4px]">
                            <Body2Regular style={{ fontWeight: 600, color: '#111827' }}>
                                템플릿 다운로드
                            </Body2Regular>
                            <CaptionLight color={colors.LAVEL_1} className="mt-[4px]">
                                이름, 휴대폰번호(필수) / 성별, 생년월일, 메모(선택) 컬럼이 포함된 엑셀 파일입니다.
                                휴대폰번호는 010-1234-5678 형식으로 입력해주세요.
                            </CaptionLight>
                            <button
                                onClick={handleDownloadPublicTemplate}
                                className="mt-[10px] self-start"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '8px 16px',
                                    borderRadius: 8,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    border: '1px solid #3B82F6',
                                    backgroundColor: '#EFF6FF',
                                    color: '#3B82F6',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#DBEAFE';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#EFF6FF';
                                }}
                            >
                                📥 퍼블릭 회원 템플릿 다운로드
                            </button>
                        </Col>
                    </Row>

                    {/* Step 2 */}
                    <Row className="gap-[14px] items-start">
                        <div
                            className="flex-shrink-0 w-[32px] h-[32px] rounded-full flex items-center justify-center"
                            style={{ backgroundColor: '#EFF6FF' }}
                        >
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#3B82F6' }}>2</span>
                        </div>
                        <Col className="flex-1 pt-[4px]">
                            <Body2Regular style={{ fontWeight: 600, color: '#111827' }}>
                                회원 정보 입력
                            </Body2Regular>
                            <CaptionLight color={colors.LAVEL_1} className="mt-[4px]">
                                다운로드한 파일의 "회원목록" 시트에 회원 정보를 입력해주세요.
                                예시 데이터는 삭제하고 실제 정보를 넣어주시면 됩니다.
                            </CaptionLight>
                        </Col>
                    </Row>

                    {/* Step 3 */}
                    <Row className="gap-[14px] items-start">
                        <div
                            className="flex-shrink-0 w-[32px] h-[32px] rounded-full flex items-center justify-center"
                            style={{ backgroundColor: '#EFF6FF' }}
                        >
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#3B82F6' }}>3</span>
                        </div>
                        <Col className="flex-1 pt-[4px]">
                            <Body2Regular style={{ fontWeight: 600, color: '#111827' }}>
                                카카오 채널로 전송
                            </Body2Regular>
                            <CaptionLight color={colors.LAVEL_1} className="mt-[4px]">
                                작성한 파일을 라포 카카오 채널로 보내주시면,
                                운영팀이 확인 후 등록을 도와드립니다.
                            </CaptionLight>
                            <a
                                href={KAKAO_CHANNEL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-[10px] self-start"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '8px 16px',
                                    borderRadius: 8,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    border: '1px solid #FEE500',
                                    backgroundColor: '#FEE500',
                                    color: '#3C1E1E',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#FADA0A';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#FEE500';
                                }}
                            >
                                💬 라포 카카오 채널 열기
                            </a>
                        </Col>
                    </Row>
                </Col>

                {/* 수강(PT) 회원 접이식 섹션 */}
                <div className="mt-[24px] border-t border-[#F3F4F6] pt-[16px]">
                    <button
                        onClick={() => setIsCoachSectionOpen(!isCoachSectionOpen)}
                        className="w-full text-left"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 14px',
                            borderRadius: 10,
                            fontSize: 13,
                            fontWeight: 500,
                            color: '#6B7280',
                            backgroundColor: '#F9FAFB',
                            border: '1px solid #E5E7EB',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F3F4F6';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#F9FAFB';
                        }}
                    >
                        <span style={{
                            display: 'inline-flex',
                            transition: 'transform 0.2s',
                            transform: isCoachSectionOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                            fontSize: 12,
                        }}>
                            ▶
                        </span>
                        담당강사가 있는 수강회원도 일괄 등록을 원하시나요?
                    </button>

                    {isCoachSectionOpen && (
                        <Col
                            className="mt-[12px] ml-[4px] p-[16px] rounded-[10px]"
                            style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}
                        >
                            <Body3Regular style={{ fontWeight: 600, color: '#065F46', fontSize: 13 }}>
                                수강(PT) 회원 일괄 등록
                            </Body3Regular>
                            <CaptionLight color="#6B7280" className="mt-[6px]">
                                담당강사, 회원명, 휴대폰번호를 입력하는 별도 템플릿입니다.
                                작성 후 동일하게 카카오 채널로 전송해주세요.
                            </CaptionLight>
                            <button
                                onClick={handleDownloadCoachTemplate}
                                className="mt-[10px] self-start"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '7px 14px',
                                    borderRadius: 8,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    border: '1px solid #059669',
                                    backgroundColor: '#ECFDF5',
                                    color: '#059669',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#D1FAE5';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#ECFDF5';
                                }}
                            >
                                📥 수강회원 템플릿 다운로드
                            </button>
                        </Col>
                    )}
                </div>

                {/* 하단 버튼 */}
                <Row
                    className="mt-[24px] justify-end"
                    style={{
                        paddingTop: 18,
                        borderTop: '1px solid #F1F5F9',
                    }}
                >
                    <button
                        onClick={onClose}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 46,
                            minWidth: 108,
                            padding: '0 18px',
                            borderRadius: 14,
                            border: '1px solid #CBD5E1',
                            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                            color: '#334155',
                            fontSize: 14,
                            fontWeight: 800,
                            cursor: 'pointer',
                            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
                            transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#94A3B8';
                            e.currentTarget.style.background = 'linear-gradient(180deg, #FFFFFF 0%, #F1F5F9 100%)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#CBD5E1';
                            e.currentTarget.style.background = 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        닫기
                    </button>
                </Row>
            </Col>
        </div>
    );
};

export default PublicMemberBulkUploadModal;
